import { useEffect, useState } from "react";
import { api, leerSesion } from "../api.ts";
import { AvisoError, useAccion } from "./comun.tsx";

interface Archivo { id: string; clinica_nombre: string; archivo: string; bytes: number; archivado_en: string; libre_desde: string | null; modo: "frase" | "llave"; automatico: boolean; filas: Record<string, number> }
interface Config { llave: { huella: string } | null; automatico: boolean; dias: number; pendientes: { id: string; nombre: string; vence: string; diasVencida: number }[]; archivos: Archivo[] }

const fecha = (iso: string | null) => (iso ? new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("es-EC", { dateStyle: "medium" }) : "—");
const mb = (n: number) => `${(n / 1048576).toFixed(1)} MB`;

/**
 * Panel de Donna → «Archivo en frío»: a los 97 días del vencimiento de un plan (7 de gracia + 90 de solo lectura) Donna sella sola una copia de la clínica con tu
 * llave PÚBLICA. El servidor no puede abrirla: solo la llave privada, que tienes tú fuera del servidor, la abre. Aquí no se pega nunca la llave privada.
 */
export function ConfigArchivoFrio() {
  const [c, setC] = useState<Config | null>(null);
  const [llave, setLlave] = useState("");
  const [msg, setMsg] = useState("");
  const a = useAccion();
  const cargar = () => api<Config>("GET", "/api/plataforma/archivo-frio-config").then(setC).catch(a.setError);
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    setMsg("");
    const r = await a.correr(() => api("PUT", "/api/plataforma/archivo-frio-config", { llave, automatico: true }));
    if (r) { setLlave(""); setMsg("Llave pública guardada y archivo automático activado."); cargar(); }
  };
  const auto = async (v: boolean) => { setMsg(""); if (await a.correr(() => api("PUT", "/api/plataforma/archivo-frio-config", { automatico: v }))) cargar(); };
  const quitar = async () => {
    if (!confirm("¿Quitar la llave pública? Se detiene el archivo automático. Los archivos ya creados se conservan y se abren con la misma llave privada.")) return;
    if (await a.correr(() => api("DELETE", "/api/plataforma/archivo-frio-config"))) { setMsg("Llave quitada."); cargar(); }
  };
  const ahora = async () => {
    setMsg("");
    const r = await a.correr(() => api<{ creados: unknown[]; errores: { clinica: string; error: string }[]; faltan: number }>("POST", "/api/plataforma/archivo-frio-config/ahora"));
    if (r) { setMsg(`Creados: ${r.creados.length}. Con error: ${r.errores.length}${r.faltan ? `. Faltan ${r.faltan} (se hacen en la siguiente vuelta)` : ""}.`); cargar(); }
  };
  const bajar = (nombre: string, blob: Blob) => { const url = URL.createObjectURL(blob); const el = document.createElement("a"); el.href = url; el.download = nombre; el.click(); setTimeout(() => URL.revokeObjectURL(url), 5000); };
  const descargar = (x: Archivo) => a.correr(async () => {
    const r = await fetch(`/api/plataforma/archivo-frio/${x.id}/descargar`, { headers: { authorization: `Bearer ${leerSesion()?.token}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "No se pudo descargar.");
    bajar(x.archivo, await r.blob());
  });
  const sobre = (x: Archivo) => a.correr(async () => {
    const s = await api<unknown>("GET", `/api/plataforma/archivo-frio/${x.id}/sobre`);
    bajar(`${x.archivo}.llave.json`, new Blob([JSON.stringify(s, null, 1)], { type: "application/json" }));
  });
  const comprobar = async (x: Archivo) => {
    setMsg("");
    const r = await a.correr(() => api<{ pacientes: number }>("POST", `/api/plataforma/archivo-frio/${x.id}/comprobar`, {}));
    if (r) { setMsg(`Archivo íntegro (su huella coincide). ${r.pacientes} pacientes.`); cargar(); }
  };

  if (!c) return <AvisoError error={a.error} />;
  return (
    <div className="archivo-frio">
      <h3 style={{ margin: "0 0 4px" }}>Archivo en frío automático</h3>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        A los <b>{c.dias} días</b> del vencimiento del plan, Donna crea sola una copia sellada y cifrada de los datos de la clínica, con tu llave <b>pública</b>. El servidor no la puede abrir: solo tu llave privada, que está fuera del servidor.
        Nunca da de baja a nadie ni borra datos. No toca clínicas de prueba, laboratorios, sin pacientes reales ni la tuya.
      </p>
      {c.llave ? (
        <div className="aviso info" role="status" style={{ marginTop: 8 }}>
          Llave pública guardada · huella <code>{c.llave.huella}</code> · Automático: <b>{c.automatico ? "activado" : "en pausa"}</b>
          <div className="fila" style={{ marginTop: 6 }}>
            <button className="btn chico" disabled={a.ocupado} onClick={() => auto(!c.automatico)}>{c.automatico ? "Pausar" : "Activar"}</button>
            <button className="btn chico" disabled={a.ocupado || !c.automatico} onClick={ahora}>Revisar ahora</button>
            <button className="btn chico peligro" disabled={a.ocupado} onClick={quitar}>Quitar la llave</button>
          </div>
        </div>
      ) : (
        <div className="aviso info" role="note" style={{ marginTop: 8 }}>Todavía no hay llave. Crea el par con <b>«Crear llave del archivo en frio.bat»</b> (carpeta herramientas\archivo-frio), guarda la privada y su frase fuera del servidor, y pega aquí SOLO la pública.</div>
      )}
      <label className="campo">{c.llave ? "Cambiar por otra llave pública (los archivos anteriores siguen necesitando la privada anterior)" : "Llave pública (empieza con -----BEGIN PUBLIC KEY-----)"}
        <textarea rows={6} value={llave} onChange={(e) => setLlave(e.target.value)} spellCheck={false} placeholder="-----BEGIN PUBLIC KEY-----" />
      </label>
      <div className="fila"><button className="btn pri" disabled={a.ocupado || !llave.trim()} onClick={guardar}>Guardar llave pública</button></div>

      <h4 style={{ marginBottom: 4 }}>Les toca ahora ({c.pendientes.length})</h4>
      {c.pendientes.length === 0 ? <p className="muted" style={{ fontSize: 13, margin: 0 }}>Ninguna clínica ha llegado al día {c.dias}.</p> : (
        <ul style={{ margin: 0 }}>{c.pendientes.map((p) => <li key={p.id}>{p.nombre} · venció el {fecha(p.vence)} ({p.diasVencida} días)</li>)}</ul>
      )}

      <h4 style={{ marginBottom: 4 }}>Últimos archivos</h4>
      {c.archivos.length === 0 && <p className="muted" style={{ fontSize: 13, margin: 0 }}>Todavía no hay archivos.</p>}
      {c.archivos.map((x) => (
        <div key={x.id} className="aviso info" role="status" style={{ marginTop: 6 }}>
          <b>{x.clinica_nombre}</b> · {fecha(x.archivado_en)} · {mb(x.bytes)} · {x.filas.pacientes ?? 0} pacientes · {x.modo === "llave" ? (x.automatico ? "automático (llave)" : "llave") : "con frase"}
          <br />Conservar hasta el <b>{fecha(x.libre_desde)}</b>
          <div className="fila" style={{ marginTop: 6 }}>
            {x.modo === "llave" && <button className="btn chico" disabled={a.ocupado} onClick={() => comprobar(x)}>Comprobar integridad</button>}
            <button className="btn chico" disabled={a.ocupado} onClick={() => descargar(x)}>Descargar archivo (cifrado)</button>
            {x.modo === "llave" && <button className="btn chico" disabled={a.ocupado} onClick={() => sobre(x)}>Descargar sobre (.llave.json)</button>}
          </div>
        </div>
      ))}
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      <AvisoError error={a.error} />
    </div>
  );
}
